import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import type { FacilityResponse, Schema } from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";

import { StatusBadge } from "@/components/StatusBadge";

import {
  approveFacility,
  rejectFacility,
  setAssessmentCenterStatus,
} from "../api";
import type {
  FacilityActionError,
  FacilityActionKind,
  FacilityActionVariables,
  FacilityActiveAction,
  FacilityReviewDialog,
  RunFacilityAction,
} from "./FacilityReviewDialogs";
import { FacilityReadinessIssues } from "./FacilityReadinessIssues";
import type { FacilityRead } from "./facilityDetailFormatters";
import {
  exactAuthoritativeData,
  governanceStateVersion,
} from "./facilityGovernanceAuthority";

type Readiness = Schema["FacilityApprovalReadinessResponse"];
const GOVERNANCE_MUTATION_KEY = ["platform", "facility-governance"] as const;

type ActionEligibility = "allowed" | "ineligible" | "no-op";

class FacilityEligibilityError extends Error {}
class FacilityNoOpError extends Error {}

function actionEligibility(
  queryClient: ReturnType<typeof useQueryClient>,
  variables: FacilityActionVariables,
): ActionEligibility {
  const facility = exactAuthoritativeData<FacilityResponse>(queryClient, [
    "platform",
    "facility",
    variables.facilityId,
  ]);
  if (!facility) return "ineligible";
  if (variables.kind === "assessment") {
    return facility.is_assessment_center === variables.payload.enabled
      ? "no-op"
      : "allowed";
  }
  if (variables.kind === "reject") {
    return facility.approval_status === "in_review" ? "allowed" : "ineligible";
  }
  const readiness = exactAuthoritativeData<Readiness>(queryClient, [
    "platform",
    "facility-approval-readiness",
    variables.facilityId,
  ]);
  return facility.approval_status === "in_review" &&
    readiness?.approval_status === "in_review" &&
    readiness.can_approve
    ? "allowed"
    : "ineligible";
}

function actionError(variables: FacilityActionVariables): FacilityActionError {
  const message =
    variables.kind === "approve"
      ? "Facility approval could not be completed. Review readiness and try again."
      : variables.kind === "reject"
        ? "Facility rejection could not be completed. Review the reason and try again."
        : "Assessment-center status could not be updated. Try again.";
  return { facilityId: variables.facilityId, kind: variables.kind, message };
}

function eligibilityError(
  variables: FacilityActionVariables,
): FacilityActionError {
  const message =
    variables.kind === "approve"
      ? "Approval eligibility changed or is refreshing. Refresh facility data before trying again."
      : variables.kind === "reject"
        ? "Facility review eligibility changed or is refreshing. Refresh facility data before trying again."
        : "Facility data is refreshing or unavailable. Try again after it finishes.";
  return { facilityId: variables.facilityId, kind: variables.kind, message };
}

export function useFacilityGovernance(facilityId: string): {
  actionError: FacilityActionError | null;
  clearActionError: (kind: FacilityActionKind) => void;
  governancePending: boolean;
  runAction: RunFacilityAction;
} {
  const queryClient = useQueryClient();
  const actionLock = useRef(false);
  const routeRef = useRef(facilityId);
  routeRef.current = facilityId;
  const [activeAction, setActiveAction] = useState<FacilityActiveAction | null>(
    null,
  );
  const [failure, setFailure] = useState<FacilityActionError | null>(null);
  const globalActionCount = useIsMutating({
    mutationKey: GOVERNANCE_MUTATION_KEY,
  });
  const subscribeToGovernanceState = useCallback(
    (notify: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        const key = event.query.queryKey;
        if (
          key.length === 3 &&
          key[0] === "platform" &&
          key[2] === facilityId &&
          (key[1] === "facility" || key[1] === "facility-approval-readiness")
        ) {
          notify();
        }
      }),
    [facilityId, queryClient],
  );
  const getGovernanceStateVersion = useCallback(
    () => governanceStateVersion(queryClient, facilityId),
    [facilityId, queryClient],
  );
  useSyncExternalStore(
    subscribeToGovernanceState,
    getGovernanceStateVersion,
    getGovernanceStateVersion,
  );

  useEffect(() => setFailure(null), [facilityId]);

  const mutation = useMutation({
    mutationKey: GOVERNANCE_MUTATION_KEY,
    mutationFn: (variables: FacilityActionVariables) => {
      const eligibility = actionEligibility(queryClient, variables);
      if (eligibility === "no-op") throw new FacilityNoOpError();
      if (eligibility === "ineligible") throw new FacilityEligibilityError();
      switch (variables.kind) {
        case "approve":
          return approveFacility(variables.facilityId);
        case "reject":
          return rejectFacility(variables.facilityId, variables.payload);
        case "assessment":
          return setAssessmentCenterStatus(
            variables.facilityId,
            variables.payload,
          );
      }
    },
    retry: false,
    onSuccess: async (response, variables) => {
      queryClient.setQueryData(
        ["platform", "facility", variables.facilityId],
        response,
      );
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: ["platform", "facility", variables.facilityId],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: [
            "platform",
            "facility-approval-readiness",
            variables.facilityId,
          ],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "facility-audit", variables.facilityId],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "org-facilities", response.organization_id],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "facilities"],
        }),
        queryClient.invalidateQueries({ queryKey: ["platform", "audit"] }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "dashboard"],
        }),
      ]);
    },
    onError: (error, variables) => {
      if (routeRef.current === variables.facilityId) {
        if (error instanceof FacilityNoOpError) return;
        setFailure(
          error instanceof FacilityEligibilityError
            ? eligibilityError(variables)
            : actionError(variables),
        );
      }
    },
    onSettled: () => {
      actionLock.current = false;
      setActiveAction(null);
    },
  });

  const runAction: RunFacilityAction = (variables, onSuccess) => {
    if (
      actionLock.current ||
      queryClient.isMutating({ mutationKey: GOVERNANCE_MUTATION_KEY }) > 0
    ) {
      return false;
    }
    const eligibility = actionEligibility(queryClient, variables);
    if (eligibility === "no-op") return false;
    if (eligibility === "ineligible") {
      if (routeRef.current === variables.facilityId) {
        setFailure(eligibilityError(variables));
      }
      return false;
    }
    actionLock.current = true;
    setFailure(null);
    setActiveAction({
      facilityId: variables.facilityId,
      kind: variables.kind,
    });
    mutation.mutate(variables, { onSuccess });
    return true;
  };

  return {
    actionError: failure?.facilityId === facilityId ? failure : null,
    clearActionError: (kind) =>
      setFailure((current) =>
        current?.facilityId === facilityId && current.kind === kind
          ? null
          : current,
      ),
    governancePending: activeAction !== null || globalActionCount > 0,
    runAction,
  };
}

function ReviewFact({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function FacilityReviewPanel({
  approvalEligible,
  facility,
  governancePending,
  readiness,
  readinessAuthoritative,
  readinessRetrying,
  rejectionEligible,
  onOpenDialog,
  onRetryReadiness,
}: {
  approvalEligible: boolean;
  facility: FacilityResponse;
  governancePending: boolean;
  readiness: FacilityRead<Readiness>;
  readinessAuthoritative: boolean;
  readinessRetrying: boolean;
  rejectionEligible: boolean;
  onOpenDialog: (dialog: Exclude<FacilityReviewDialog, null>) => void;
  onRetryReadiness: () => void;
}) {
  const inReview = facility.approval_status === "in_review";
  const blockers = readiness.data?.blockers ?? [];
  const warnings = readiness.data?.warnings ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="facility-review-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            alignItems: { sm: "flex-start" },
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography
                id="facility-review-heading"
                component="h2"
                variant="h6"
                sx={{ fontWeight: 700 }}
              >
                Approval review
              </Typography>
              <StatusBadge status={facility.approval_status} />
            </Stack>
            <Box
              component="dl"
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2.5,
                m: 0,
                mt: 1.5,
              }}
            >
              <ReviewFact
                label="Submitted"
                value={
                  facility.approval_submitted_at
                    ? formatDate(facility.approval_submitted_at)
                    : "Not submitted"
                }
              />
              <ReviewFact
                label="Reviewed"
                value={
                  facility.approval_reviewed_at
                    ? formatDate(facility.approval_reviewed_at)
                    : "Not reviewed"
                }
              />
            </Box>
          </Box>

          {inReview && (
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant="contained"
                disabled={governancePending || !approvalEligible}
                onClick={() => onOpenDialog("approve")}
              >
                Approve facility
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={governancePending || !rejectionEligible}
                onClick={() => onOpenDialog("reject")}
              >
                Reject facility
              </Button>
            </Stack>
          )}
        </Stack>

        {facility.approval_rejection_reason && (
          <Alert severity="error" variant="outlined">
            <Typography
              variant="caption"
              sx={{ display: "block", fontWeight: 700 }}
            >
              Rejection reason
            </Typography>
            {facility.approval_rejection_reason}
          </Alert>
        )}

        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
          {readiness.state === "loading" ? (
            <Box
              role="status"
              aria-label="Loading approval readiness"
              aria-live="polite"
              sx={{ display: "flex", justifyContent: "center", py: 3 }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : readiness.state === "error" ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={readiness.onRetry}
                >
                  Retry
                </Button>
              }
            >
              Approval readiness could not be loaded. Try again.
            </Alert>
          ) : !readinessAuthoritative ? (
            <Alert
              severity="warning"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={readinessRetrying || governancePending}
                  onClick={onRetryReadiness}
                >
                  Retry
                </Button>
              }
            >
              Approval readiness is no longer current. Refresh it before
              approving this facility.
            </Alert>
          ) : blockers.length === 0 && warnings.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No approval readiness issues.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <FacilityReadinessIssues title="Blockers" issues={blockers} />
              <FacilityReadinessIssues title="Warnings" issues={warnings} />
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
